
import React, { useEffect, useState } from "react";

const EnglishQuizDeleteDialog = ({
  isOpen,
  quiz,
  onConfirm,
  onClose,
  isLoading = false,
  error = null,
  requireNameConfirmation = true,
}) => {
  const [confirmationText, setConfirmationText] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setConfirmationText("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isLoading) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) {
    return null;
  }

  const quizName =
    quiz && quiz.name ? quiz.name : "Untitled English Quiz";

  const subject =
    quiz && quiz.questionBankSubject
      ? quiz.questionBankSubject
      : quiz && quiz.subject
        ? quiz.subject
        : "English";

  const status =
    quiz && quiz.status ? quiz.status : "draft";

  const duration =
    quiz && quiz.durationMinutes !== undefined
      ? quiz.durationMinutes
      : null;

  const totalQuestions =
    quiz && quiz.totalQuestions !== undefined
      ? quiz.totalQuestions
      : quiz && quiz.questions
        ? quiz.questions.length
        : null;

  const totalMarks =
    quiz && quiz.totalMarks !== undefined
      ? quiz.totalMarks
      : null;

  const normalizedQuizName = String(quizName)
    .trim()
    .toLowerCase();

  const normalizedConfirmationText = String(
    confirmationText,
  )
    .trim()
    .toLowerCase();

  const nameConfirmed =
    normalizedConfirmationText === normalizedQuizName;

  const canDelete =
    !isLoading &&
    (!requireNameConfirmation || nameConfirmed);

  let errorMessage =
    "Something went wrong. Please try again.";

  if (typeof error === "string") {
    errorMessage = error;
  } else if (
    error &&
    error.data &&
    error.data.message
  ) {
    errorMessage = error.data.message;
  } else if (error && error.message) {
    errorMessage = error.message;
  }

  const handleConfirm = async () => {
    if (!canDelete) {
      return;
    }

    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="english-quiz-delete-dialog-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close dialog"
        disabled={isLoading}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-slate-900/60 backdrop-blur-sm disabled:cursor-not-allowed"
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 6h18"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 6V4h8v2"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 6l-1 14H6L5 6"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 11v5"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 11v5"
                />
              </svg>
            </div>

            <div className="min-w-0 flex-1">
              <h2
                id="english-quiz-delete-dialog-title"
                className="text-lg font-semibold text-slate-900"
              >
                Delete English Quiz
              </h2>

              <p className="mt-1 text-sm leading-5 text-slate-500">
                This action permanently removes the quiz.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Warning */}
          <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mt-0.5 h-5 w-5 shrink-0 text-red-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v4"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 17h.01"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m10.3 4.3-7.5 12.7a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z"
              />
            </svg>

            <div>
              <p className="text-sm font-semibold text-red-900">
                Permanent deletion
              </p>

              <p className="mt-1 text-sm leading-5 text-red-800">
                Deleting this quiz may permanently remove its
                configuration and associated data. Make sure
                you no longer need this quiz before continuing.
              </p>
            </div>
          </div>

          {/* Quiz Summary */}
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Quiz to delete
              </p>

              <h3 className="mt-1 break-words text-base font-semibold text-slate-900">
                {quizName}
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">
                  Subject
                </p>

                <p className="mt-1 truncate text-sm font-medium text-slate-800">
                  {subject}
                </p>
              </div>

              <div className="min-w-0">
                <p className="text-xs text-slate-500">
                  Status
                </p>

                <span className="mt-1 inline-flex max-w-full truncate rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium capitalize text-slate-700">
                  {status}
                </span>
              </div>

              <div>
                <p className="text-xs text-slate-500">
                  Questions
                </p>

                <p className="mt-1 text-sm font-medium text-slate-800">
                  {totalQuestions !== null
                    ? totalQuestions
                    : "—"}
                </p>
              </div>

              <div>
                <p className="text-xs text-slate-500">
                  Marks
                </p>

                <p className="mt-1 text-sm font-medium text-slate-800">
                  {totalMarks !== null
                    ? totalMarks
                    : "—"}
                </p>
              </div>
            </div>

            {duration !== null && (
              <div className="mt-4 border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500">
                  Duration
                </p>

                <p className="mt-1 text-sm font-medium text-slate-800">
                  {duration} minutes
                </p>
              </div>
            )}
          </div>

          {/* Name Confirmation */}
          {requireNameConfirmation && (
            <div className="mt-5">
              <label
                htmlFor="english-quiz-delete-confirmation"
                className="block text-sm font-medium text-slate-800"
              >
                Type the quiz name to confirm
              </label>

              <p className="mt-1 text-xs leading-5 text-slate-500">
                Enter{" "}
                <span className="font-semibold text-slate-700">
                  {quizName}
                </span>{" "}
                exactly as shown above.
              </p>

              <input
                id="english-quiz-delete-confirmation"
                type="text"
                value={confirmationText}
                onChange={(event) =>
                  setConfirmationText(
                    event.target.value,
                  )
                }
                disabled={isLoading}
                autoComplete="off"
                placeholder={quizName}
                className={
                  "mt-3 block w-full rounded-lg border bg-white px-3 py-2.5 " +
                  "text-sm text-slate-900 outline-none transition " +
                  "placeholder:text-slate-400 focus:ring-2 " +
                  "disabled:cursor-not-allowed disabled:bg-slate-100 " +
                  (
                    confirmationText && !nameConfirmed
                      ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                      : nameConfirmed
                        ? "border-emerald-300 focus:border-emerald-500 focus:ring-emerald-100"
                        : "border-slate-300 focus:border-red-500 focus:ring-red-100"
                  )
                }
              />

              {confirmationText && !nameConfirmed && (
                <p className="mt-2 text-xs text-red-600">
                  The quiz name does not match.
                </p>
              )}

              {nameConfirmed && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m5 12 4 4L19 6"
                    />
                  </svg>

                  Quiz name confirmed.
                </p>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 shrink-0 text-red-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 8v5"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 16h.01"
                  />
                </svg>

                <div>
                  <p className="text-sm font-medium text-red-800">
                    Unable to delete quiz
                  </p>

                  <p className="mt-1 text-sm text-red-700">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canDelete}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />

                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4Z"
                />
              </svg>
            )}

            {isLoading ? "Deleting..." : "Delete Quiz"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnglishQuizDeleteDialog;

